/**
 * SnapchatStoryAdMockup - Snapchat Story Ad 9:16 Carousel Mockup
 *
 * Dimensions: 350px x 622px (9:16 aspect ratio)
 *
 * Structure (matching Snapchat Story Ad format):
 * - Top Progress Bars: Multiple bars showing carousel position
 * - Header: Profile picture, brand name, "Sponsored", three dots menu
 * - Full-screen Background: Current 9:16 slide image/video
 * - Navigation: Left/right arrows on hover
 * - Footer: Grey info box (left) with brand/headline/url, Ad badge (right)
 *
 * Usage:
 *   <SnapchatStoryAdMockup
 *     username="Ford"
 *     items={[
 *       { imageUrl: '/path/to/image1.jpg', headline: 'Slide 1' },
 *       { imageUrl: '/path/to/image2.jpg', headline: 'Slide 2' },
 *     ]}
 *     onSlideChange={(index) => console.log('Slide:', index)}
 *   />
 */

import { useState, useEffect, useRef } from 'react';
import { MediaDisplay } from './MediaDisplay';

interface CarouselItem {
  imageUrl?: string;
  headline?: string;
}

interface SnapchatStoryAdMockupProps {
  username?: string;
  profilePicUrl?: string;
  items?: CarouselItem[];
  activeIndex?: number;
  onSlideChange?: (index: number) => void;
}

export function SnapchatStoryAdMockup({
  username = 'fordmotorcompany',
  profilePicUrl,
  items = [
    { imageUrl: '', headline: 'Slide 1' },
    { imageUrl: '', headline: 'Slide 2' },
    { imageUrl: '', headline: 'Slide 3' },
  ],
  activeIndex,
  onSlideChange,
}: SnapchatStoryAdMockupProps) {
  const [currentIndex, setCurrentIndex] = useState(activeIndex ?? 0);
  const isSyncingFromParent = useRef(false);

  useEffect(() => {
    if (activeIndex !== undefined && activeIndex !== currentIndex) {
      isSyncingFromParent.current = true;
      setCurrentIndex(activeIndex);
    }
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSyncingFromParent.current) {
      isSyncingFromParent.current = false;
      return;
    }
    onSlideChange?.(currentIndex);
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const currentItem = items[currentIndex];

  return (
    <div className="relative w-full h-full bg-black overflow-hidden group">
      {/* Full-screen Background Image/Video */}
      <div className="absolute inset-0">
        {currentItem?.imageUrl ? (
          <MediaDisplay
            url={currentItem.imageUrl}
            alt={currentItem.headline || `Slide ${currentIndex + 1}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: '#1a1a1a' }}>
            <div className="text-center text-white/50">
              <div className="text-4xl font-bold mb-2">9:16</div>
              <div className="text-sm">Slide {currentIndex + 1} of {items.length}</div>
              <div className="text-xs mt-1">1080x1920px recommended</div>
            </div>
          </div>
        )}
      </div>

      {/* Story progress bars at very top - one per slide */}
      <div className="absolute top-2 left-3 right-3 z-30 flex gap-1">
        {items.map((_, idx) => (
          <div
            key={idx}
            className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden cursor-pointer"
            onClick={() => setCurrentIndex(idx)}
          >
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                idx < currentIndex
                  ? 'w-full bg-white'
                  : idx === currentIndex
                  ? 'w-full bg-white'
                  : 'w-0 bg-white'
              }`}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between"
        style={{ padding: '16px 12px 0' }}
      >
        <div className="flex items-center" style={{ gap: '12px' }}>
          {/* Profile Picture */}
          <div
            className="rounded-full overflow-hidden bg-gray-600 flex items-center justify-center"
            style={{
              width: '40px',
              height: '40px',
              border: '2px solid #fff',
            }}
          >
            {profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt={username}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-bold">
                {username.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          {/* Username & Sponsored */}
          <div>
            <h4
              className="text-white font-semibold m-0"
              style={{ fontSize: '14px' }}
            >
              {username}
            </h4>
            <span
              className="text-white/90"
              style={{ fontSize: '12px' }}
            >
              Sponsored
            </span>
          </div>
        </div>
        {/* Three Dots Menu */}
        <div>
          <svg width="6" height="21" viewBox="0 0 6 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="3" cy="3.50001" rx="3" ry="3.00001" fill="#F9F9F9"/>
            <ellipse cx="3" cy="10.5" rx="3" ry="3.00001" fill="#F9F9F9"/>
            <ellipse cx="3" cy="17.5" rx="3" ry="3.00001" fill="#F9F9F9"/>
          </svg>
        </div>
      </div>

      {/* Navigation Arrows - Show on hover */}
      {items.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </>
      )}

      {/* Footer - Grey info box (left) and Ad badge (right) on same row */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20"
        style={{
          padding: '16px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '12px'
        }}
      >
        {/* Grey Info Box - left side */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(80, 80, 80, 0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderRadius: '12px',
          padding: '10px 12px',
          flex: 1,
          minWidth: 0,
          maxWidth: 'calc(100% - 60px)'
        }}>
          {/* Brand info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              marginBottom: '2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {username}
            </div>
            {currentItem?.headline && (
              <div style={{
                color: '#fff',
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {currentItem.headline}
              </div>
            )}
          </div>
          {/* Chevron arrow */}
          <div style={{
            marginLeft: '10px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Ad Badge - right side */}
        <div style={{
          background: 'rgba(255,255,255,0.2)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          padding: '6px 14px',
          borderRadius: '9999px',
          color: '#fff',
          fontSize: '13px',
          fontWeight: 600,
          flexShrink: 0
        }}>
          Ad
        </div>
      </div>
    </div>
  );
}
